import React, { useContext, useMemo } from 'react';
import { ProductTransaction, ProductTransactionType } from '../../../../../../../../types/productTransaction';
import { t } from '../../../../../../../../types/translation/Translator';
import { TranslationKey } from '../../../../../../../../types/translation/TranslationKey';
import { ProductMasterData } from '../../../../../../../../types/product_master_data/productMasterData';
import { ProductMasterDataContext } from '../../../../../../../../context/ProductMasterDataContext';
import { KitTemplateContext } from '../../../../../../../../context/KitTemplateContext';
import { DropdownSearchSelect } from '../../../../../../../components/Dropdown';
import { KitContext } from '../../../../../../../../context/KitContext';
import { ProductContext } from '../../../../../../../../context/ProductContext';

interface ProductTransactionSourceKitProps {
  transaction: ProductTransaction;
  setTransaction: React.Dispatch<React.SetStateAction<ProductTransaction>>;
  productMasterData?: ProductMasterData;
  disabled?: boolean;
}

export default function ProductTransactionSourceKit({
  transaction,
  setTransaction,
  productMasterData: pmd,
  disabled = false,
}: ProductTransactionSourceKitProps) {
  if (transaction.type === ProductTransactionType.inbound) return null;
  if (!pmd) return null;

  const { productMasterData } = useContext(ProductMasterDataContext);
  const { kitTemplates } = useContext(KitTemplateContext);
  const { kits } = useContext(KitContext);
  const { products } = useContext(ProductContext);

  const kitManaged = pmd.kitManaged;

  const kitIdsInSelectedBin = useMemo(() => {
    if (!transaction.product.fromBinId) return new Set<string>();

    return new Set(
      [...(products.get(pmd.id)?.values() || [])]
        .filter(
          product => product.companyId === transaction.companyId && product.binId === transaction.product.fromBinId,
        )
        .map(product => product.kitId)
        .filter((kitId): kitId is string => !!kitId),
    );
  }, [products, pmd.id, transaction.product.fromBinId]);

  const items = useMemo(() => {
    const templateIds = new Set(
      [...kitTemplates.values()]
        .filter(kt => kt.companyId === transaction.companyId && kt.productMasterDataId === pmd.id)
        .map(kt => kt.id),
    );

    return [...kits.values()].filter(
      k => k.companyId === transaction.companyId && templateIds.has(k.templateId) && kitIdsInSelectedBin.has(k.id),
    );
  }, [kitTemplates, kits, pmd.id, kitIdsInSelectedBin]);

  const potentialKits = useMemo(() => {
    const templates = [...kitTemplates.values()].filter(kt => kt.products.some(p => p.productMasterDataId === pmd.id));
    return [...kits.values()].filter(
      kit =>
        kit.companyId === transaction.companyId &&
        templates.some(template => template.id === kit.templateId) &&
        kitIdsInSelectedBin.has(kit.id),
    );
  }, [kits, kitTemplates, pmd.id, kitIdsInSelectedBin]);

  if (!kitManaged && !potentialKits.length) return null;

  return (
    <div className='w-full p-2'>
      <div className='grid grid-cols-1 gap-2'>
        <div className='grid grid-cols-2 gap-2'>
          <DropdownSearchSelect
            mandatory={pmd.kitManaged}
            disabled={disabled}
            label={t(TranslationKey.kit).singular.label}
            placeholder={t(TranslationKey.kit).singular.label}
            items={pmd.kitManaged ? items : potentialKits}
            textBuilder={item => kits.get(item.id)?.identifier || ''}
            value={kits.get(transaction.product.kit?.fromKitId || '') || transaction.metadata?.fromKit}
            onSelect={item => {
              setTransaction(transaction => transaction.withFromKitId(item?.id));
              setTransaction(transaction => transaction.withKitTemplateId(item?.templateId));
              setTransaction(transaction => transaction.withKitComplete(true));
              if (transaction.type === ProductTransactionType.move) {
                setTransaction(transaction => transaction.withToKitId(item?.id));
              }
            }}
          />
        </div>
      </div>
    </div>
  );
}
